import { Box, Container, Grid } from "@mui/material";
import CategorySectionCarousel from "components/category-section-carousel/categorySectionCarousel";
import CategorySectionHeader from "components/CategorySectionHeader";
import GalleryBuildItem from "components/gallery/GalleryBuildItem";
import ShopLayout1 from "components/layouts/ShopLayout1";
import LazyImage from "components/LazyImage";
import { PcBundleCard } from "components/product-cards/PcBundleCard";
import LoadingSkeleton from "components/skeletons/SkeletonLoading";
import SwiperCarousel from "components/swiper/SwiperCarousel";
import { H1 } from "components/Typography";
import { useIsMobile } from "hooks";
import useFetchCategoryItems from "hooks/powered-by-brands/use-fetch-category-items";
import useRenderData from "hooks/powered-by-brands/use-render-logic";
import { useRouter } from "next/router";
import React, { FC, useEffect, useState } from "react";

interface Props {}

const initialRequestParams = {
  keyword: "MSI",
  limit: 10,
  filter: {
    cpu: "",
    build_tier: "",
    price_range: "0,999999",
  },
};

const AsusView: FC<Props> = () => {
  const isMobile = useIsMobile();
  const { push } = useRouter();
  const [isRendering, setIsRendering] = useState(true);

  const { productItems, isProductLoading, facets } =
    useFetchCategoryItems("MSI");

  const {
    isBundleLoading,
    bundleList,
    isBuildsGalleryLoading,
    buildsGalleryItems,
    isPcBuildsRendering,
  } = useRenderData("MSI", initialRequestParams);

  useEffect(() => {
    if (
      !Object.values(isProductLoading).some((loading) => loading) &&
      Object.keys(productItems).length > 0
    ) {
      setIsRendering(false);
    }
  }, [isProductLoading, productItems]);

  const breakpoints = {
    0: { slidesPerView: 2.25 },
    600: { slidesPerView: 3.25 },
    900: { slidesPerView: 4.25 },
    1200: { slidesPerView: 5.25 },
    1536: { slidesPerView: 6.25 },
  };

  const breakpointsNoResultPage = {
    0: { slidesPerView: 1.5 },
    600: { slidesPerView: 1.5 },
    900: { slidesPerView: 4.25 },
    1200: { slidesPerView: 4.25 },
    1536: { slidesPerView: 4.25 },
  };

  return (
    <ShopLayout1>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "25.38%",
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <LazyImage
          src={"/assets/images/powered_by_msi.png"}
          alt="Powered By MSI"
          width={0}
          height={0}
          objectFit="cover"
          layout="fill"
        />
      </Box>

      {/* Conditionally render the PC Bundle container */}
      {bundleList?.length > 0 && (
        <Container style={{ margin: "10px auto 5px auto" }}>
          <H1>Powered Builds By MSI</H1>

          {/* Render Asus PC Bundle Cards */}
          <LoadingSkeleton isLoading={isBundleLoading} />
          <Grid container spacing={1.5} sx={{ mt: 1 }}>
            {!isBundleLoading &&
              bundleList.map((pc_bundle, index) => (
                <Grid key={index} item xs={12} sm={6} md={3}>
                  <PcBundleCard
                    pc_bundle_data={pc_bundle}
                    onHandleNavigateDetails={() => {
                      const url = `/pc-bundle/details/${pc_bundle.pc_bundle_id}?brand=MSI&build=${pc_bundle.pc_bundle_slug}`;
                      push(url);
                    }}
                  />
                </Grid>
              ))}
          </Grid>
        </Container>
      )}

      <LoadingSkeleton isLoading={isPcBuildsRendering} />
      {!isPcBuildsRendering && (
        <Container style={{ margin: "50px auto 0px auto", padding: "0 10px" }}>
          <CategorySectionHeader
            title="PC Builds"
            seeMoreLinkTxt="See More"
            seeMoreLink={`/builds-gallery?keyword=MSI`}
          />
          <SwiperCarousel
            id="latestBuilds"
            data={buildsGalleryItems}
            renderItem={(item) => <GalleryBuildItem data={item} />}
            isLoading={isBuildsGalleryLoading}
            breakpoints={isMobile ? breakpointsNoResultPage : breakpoints}
          />
        </Container>
      )}

      <LoadingSkeleton isLoading={isPcBuildsRendering} />
      {!isRendering && (
        <CategorySectionCarousel
          manufacturer="MSI"
          productItems={productItems}
          isProductLoading={isProductLoading}
          isMobile={isMobile}
          breakpoints={breakpoints}
          breakpointsNoResultPage={breakpointsNoResultPage}
          facets={facets}
        />
      )}
    </ShopLayout1>
  );
};

export default AsusView;
